import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ToolsRegistryApi } from './ToolsRegistryApi';
import type { McpTool, McpServerRecord, McpServerInput, CopilotPersona, PersonaInput } from './types';

export class ToolsRegistryClient implements ToolsRegistryApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async base(): Promise<string> {
    return this.discoveryApi.getBaseUrl('copilot');
  }

  async getTools(): Promise<McpTool[]> {
    const baseUrl = await this.base();
    const response = await this.fetchApi.fetch(`${baseUrl}/tools`);
    if (!response.ok) throw new Error(`Failed to fetch tools (HTTP ${response.status})`);
    const data = await response.json();
    return data.tools as McpTool[];
  }

  async getPersonas(): Promise<CopilotPersona[]> {
    const baseUrl = await this.base();
    const response = await this.fetchApi.fetch(`${baseUrl}/personas`);
    if (!response.ok) throw new Error(`Failed to fetch personas (HTTP ${response.status})`);
    const data = await response.json();
    return data.personas as CopilotPersona[];
  }

  async getPersona(id: string): Promise<CopilotPersona> {
    const baseUrl = await this.base();
    const response = await this.fetchApi.fetch(`${baseUrl}/personas/${encodeURIComponent(id)}`);
    if (!response.ok) throw new Error(`Failed to fetch persona (HTTP ${response.status})`);
    return response.json();
  }

  async createPersona(input: PersonaInput): Promise<{ id: string }> {
    const baseUrl = await this.base();
    const response = await this.fetchApi.fetch(`${baseUrl}/personas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as any).error ?? `Failed to create persona (HTTP ${response.status})`);
    }
    return response.json();
  }

  async updatePersona(id: string, input: Partial<PersonaInput>): Promise<void> {
    const baseUrl = await this.base();
    const response = await this.fetchApi.fetch(`${baseUrl}/personas/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as any).error ?? `Failed to update persona (HTTP ${response.status})`);
    }
  }

  async deletePersona(id: string): Promise<void> {
    const baseUrl = await this.base();
    const response = await this.fetchApi.fetch(`${baseUrl}/personas/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as any).error ?? `Failed to delete persona (HTTP ${response.status})`);
    }
  }

  /* ── MCP Server marketplace ──────────────────────────────────────────── */

  async getServers(): Promise<McpServerRecord[]> {
    const baseUrl = await this.base();
    const response = await this.fetchApi.fetch(`${baseUrl}/mcp-servers`);
    if (!response.ok) throw new Error(`Failed to fetch MCP servers (HTTP ${response.status})`);
    const data = await response.json();
    return (data.servers as any[]).map(s => ({
      id: s.id,
      name: s.name,
      description: s.description ?? '',
      transport: s.transport,
      url: s.url,
      enabled: s.enabled,
      isDefault: s.is_default,
      docsUrl: s.docs_url,
      createdAt: s.created_at,
      updatedAt: s.updated_at,
    }));
  }

  async getAllServers(): Promise<McpServerRecord[]> {
    const baseUrl = await this.base();
    const response = await this.fetchApi.fetch(`${baseUrl}/mcp-servers/all`);
    if (!response.ok) throw new Error(`Failed to fetch MCP servers (HTTP ${response.status})`);
    const data = await response.json();
    return (data.servers as any[]).map(s => ({
      id: s.id,
      name: s.name,
      description: s.description ?? '',
      transport: s.transport,
      url: s.url,
      enabled: s.enabled,
      isDefault: s.is_default,
      docsUrl: s.docs_url,
      createdAt: s.created_at,
      updatedAt: s.updated_at,
    }));
  }

  async createServer(input: McpServerInput): Promise<{ id: string }> {
    const baseUrl = await this.base();
    const response = await this.fetchApi.fetch(`${baseUrl}/mcp-servers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as any).error ?? `Failed to create MCP server (HTTP ${response.status})`);
    }
    return response.json();
  }

  async updateServer(id: string, input: Partial<McpServerInput & { enabled?: boolean }>): Promise<void> {
    const baseUrl = await this.base();
    const response = await this.fetchApi.fetch(`${baseUrl}/mcp-servers/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as any).error ?? `Failed to update MCP server (HTTP ${response.status})`);
    }
  }

  async deleteServer(id: string): Promise<void> {
    const baseUrl = await this.base();
    const response = await this.fetchApi.fetch(`${baseUrl}/mcp-servers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as any).error ?? `Failed to delete MCP server (HTTP ${response.status})`);
    }
  }

  async getPersonaServers(personaId: string): Promise<string[]> {
    const baseUrl = await this.base();
    const response = await this.fetchApi.fetch(`${baseUrl}/mcp-servers/persona/${encodeURIComponent(personaId)}`);
    if (!response.ok) throw new Error(`Failed to fetch persona servers (HTTP ${response.status})`);
    const data = await response.json();
    return data.serverIds;
  }

  async setPersonaServers(personaId: string, serverIds: string[]): Promise<void> {
    const baseUrl = await this.base();
    const response = await this.fetchApi.fetch(`${baseUrl}/mcp-servers/persona/${encodeURIComponent(personaId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ serverIds }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as any).error ?? `Failed to set persona servers (HTTP ${response.status})`);
    }
  }
}
